import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, FormControl, InputLabel, Select, MenuItem, Dialog, DialogContent, Typography } from '@mui/material';

import { ProtectedRouteContext } from '../Context';
import Button from '../shared/Button';
import View from '../shared/View';
import Form from '../shared/Form';
import Email from '../shared/Email';
import Phone from '../shared/Phone';
import Name from '../shared/Name';
import PanelSection from '../shared/PanelSection';
import Panel from '../shared/Panel';
import LabeledInput from '../shared/LabeledInput';
import LabeledSelect from '../shared/LabeledSelect';
import DatePicker from '../shared/datePick/DatePick';
import FileDrop from '../shared/FileDrop';
import FeesTable from '../shared/feesTable';
import RenderIf from '../renderIf';
import { MuiStyledInput, RequiredMarker } from '../shared/muiControls';

import useSchoolInfo from './schoolInfo/useSchoolInfo';

import './SchoolInfo.scss';

export default function SchoolInfo(props) {
    const { displayModal, userSettings } = useContext(ProtectedRouteContext);

    const [isInitialized, setIsInitialized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fileSaving, setFileSaving] = useState(false);
    const [delAffidavit, setDelAffidavit] = useState(false);
    const [affidavit, setAffidavit] = useState([]);
    const [formState, setFormState] = useState();

    const [selectedTerm, setSelectedTerm] = useState('');
    const [terms, setTerms] = useState([]);

    const formRefs = useRef([]);

    const {
        //States
        state: quarterDateState,

        //Handlers
        init,
        handleDateChange,
        handleDateBlur,
        handleDateError,
        handleDayChange,

        //Styles
        dateTextFieldSx,
        dateLabelSx,
    } = useSchoolInfo();

    const addToRefs = (el) => {
        if (el && !formRefs.current.includes(el)) {
            formRefs.current.push(el);
        }
    };

    const handleFormState = (responseObj) => {
        let _formState = {
            schoolInfo: responseObj.schoolInfo,
        };

        let _affidavit = responseObj?.affidavit
            ? [
                  {
                      name: responseObj.affidavit.SchoolFileDescription,
                      fileId: responseObj.affidavit.FileID,
                      uploadedOn: new Date(responseObj.affidavit.UploadedOn).toISOString(),
                      type: 'pdf',
                  },
              ]
            : [];

        setFormState(_formState);

        init(_formState.schoolInfo);

        setAffidavit(_affidavit);
    };

    useEffect(() => {
        if (userSettings.Retrieved && !isInitialized) {
            let retrieveSchoolData = async () => {
                try {
                    let { currentTermID } = await API.get('ShipyardAPI', '/applications/application_term');

                    let [response, { Terms }] = await Promise.all([
                        API.get('ShipyardAPI', '/schoolsettings', {
                            queryStringParameters: {
                                schoolId: userSettings.SchoolId,
                                termId: currentTermID,
                            },
                        }),
                        API.get('ShipyardAPI', '/applications/terms'),
                    ]);

                    setTerms(Terms);
                    setSelectedTerm(`${currentTermID}`);

                    handleFormState(response);
                } catch (e) {
                    console.error(e);
                }

                setIsInitialized(true);
                setLoading(false);
            };

            retrieveSchoolData();
        }
    }, [loading, userSettings.SchoolId, userSettings.Retrieved]);

    useEffect(() => {
        if (delAffidavit) {
            setDelAffidavit(false);

            const deleteFile = async () => {
                try {
                    await API.del('ShipyardAPI', '/schoolsettings/affidavit', {
                        body: {
                            fileId: affidavit[0].fileId,
                            status: '0',
                        },
                    });

                    setAffidavit([]);
                } catch (e) {
                    toast.error(t('General.AjaxError'));
                }
            };

            deleteFile();
        }
    }, [delAffidavit, affidavit]);

    const handleSave = (newformState) => {
        setFormState((prev) => {
            let _formState = Object.assign({}, prev);

            for (const [key, value] of Object.entries(newformState)) {
                if(key.includes('NumDays')) _formState.schoolInfo[key] = parseInt(value) || '';
                else _formState.schoolInfo[key] = value;
            }

            return _formState;
        });
    };

    const handleValidSave = async () => {
        setSaving(true);

        let parsedQuarterDateState = {};
        for (let key in quarterDateState) {
            if (key.slice(key.length - 3) === 'Utc')
                parsedQuarterDateState[key[0].toUpperCase() + key.slice(1, key.length - 3) + key.slice(key.length - 3).toUpperCase()] =
                    quarterDateState[key]?.toISOString?.() || '';
            else continue;
        }

        try {
            await API.patch('ShipyardAPI', '/schoolsettings', {
                body: {
                    ...formState.schoolInfo,
                    ...parsedQuarterDateState,
                    termId: selectedTerm,
                },
            });

            toast.success('School settings saved.');
        } catch (e) {
            toast.error(t('General.AjaxError'));
        } finally {
            setSaving(false);
        }
    };

    const handleDrop = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();

        setFileSaving(true);

        let _fileDropped = event?.dataTransfer?.files || event?.target?.files;

        let s3Call;
        try {
            s3Call = await API.get('ShipyardAPI', `/schoolsettings/affidavit_presign`, {
                queryStringParameters: {
                    fileName: _fileDropped[0].name,
                    documentType: 6,
                    mimeType: _fileDropped[0].type,
                },
            });
        } catch (e) {
            toast.error(t('General.AjaxError'));

            setFileSaving(false);

            return;
        }

        //fetch() call here to the URL provided by the above API call
        let fetchCall;
        try {
            let options = {
                body: _fileDropped[0],
                headers: {
                    'Content-Type': '',
                },
                method: 'PUT',
            };

            fetchCall = await fetch(s3Call.url, options);

            if (!fetchCall.ok) {
                toast.error(t('General.AjaxError'));

                setFileSaving(false);

                return;
            }
        } catch (e) {
            toast.error(t('General.AjaxError'));

            setFileSaving(false);

            return;
        }

        try {
            await API.put('ShipyardAPI', `/schoolsettings/affidavit`, {
                queryStringParameters: {
                    schoolId: formState.schoolInfo.SchoolID,
                    fileId: s3Call.fileId,
                    status: '1', //Uploaded
                },
            });
        } catch (e) {
            toast.error(t('General.AjaxError'));

            setFileSaving(false);

            return;
        }

        let _file = {
            name: _fileDropped[0].name,
            fileId: s3Call.fileId,
            uploadedOn: new Date().toISOString(),
            type: _fileDropped[0].type,
        };

        setAffidavit([_file]);

        setFileSaving(false);
    };

    const handleDrag = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
    };

    const handleRemove = () => {
        let param2 = () => (
            <Dialog open={true}>
                <DialogContent className="deletion-warning-container">
                    <Box className="deletion-warning-text">
                        <label>Are you sure you want to delete:</label>
                        <br />
                        {affidavit[0].name}
                    </Box>
                    <Box>
                        <Button
                            useMui={true}
                            type="negative"
                            onClick={() => {
                                displayModal(false, null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            useMui={true}
                            onClick={() => {
                                displayModal(false, null);
                                setDelAffidavit(true);
                            }}
                        >
                            Continue
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        );
        displayModal(true, param2);
    };

    const optionJSX = useMemo(() => {
        return typeof formState?.schoolInfo?.stateOptions?.map === 'function'
            ? formState.schoolInfo.stateOptions.map((opt) => {
                  return (
                      <option
                          value={opt.StateID}
                          key={`option-${opt.StateID}`}
                      >
                          {opt.StateName}
                      </option>
                  );
              })
            : null;
    }, [formState]);

    const handleTerm = async (value) => {
        setLoading(true);
        setSelectedTerm(value);

        try {
            let response = await API.get('ShipyardAPI', '/schoolsettings', {
                queryStringParameters: {
                    schoolId: userSettings.SchoolId,
                    termId: value,
                },
            });

            handleFormState(response);
        } catch (e) {
            toast.error('API error.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <View
            viewTitle={
                <h2>
                    <ReactMarkdown
                        disallowedElements={['p']}
                        unwrapDisallowed
                    >
                        {t('SchoolInfo.Header')}
                    </ReactMarkdown>
                </h2>
            }
            doingAjax={loading || saving}
            name="school-info"
        >
            <Form
                refs={formRefs.current}
                primaryButton={true}
                primaryButtonText={t('UserSettings.SaveButtonText')}
                primaryButtonInteractiveText={t('General.InteractiveSave')}
                onUserInteract={{ handleSave: handleSave }}
                onValidSave={handleValidSave}
                onInvalidSave={() => {
                    setSaving(false);
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                    <FormControl
                        sx={{ m: 1, minWidth: 240, marginRight: (theme) => theme.spacing(6) }}
                        size="small"
                    >
                        <InputLabel
                            id="term-label"
                            sx={{ fontSize: '16px' }}
                        >
                            Term
                        </InputLabel>
                        <Select
                            label="Term"
                            labelId="term-label"
                            value={selectedTerm}
                            onChange={(event) => handleTerm(event.target.value)}
                            disabled={loading}
                        >
                            <MenuItem
                                key="_blank"
                                value=""
                            ></MenuItem>
                            {terms.map((term, i) => {
                                return (
                                    <MenuItem
                                        key={`term-${i}`}
                                        value={term.value}
                                    >
                                        {term.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>

                <Panel>
                    <PanelSection
                        classes={['school-info']}
                        headerText={t('SchoolInfo.SchoolInformationHeader')}
                    >
                        <LabeledInput
                            value={!!formState?.schoolInfo?.SchoolName ? formState.schoolInfo.SchoolName : ''}
                            labelText={t('SchoolInfo.SchoolNameLabel')}
                            placeholder={t('SchoolInfo.SchoolNamePlaceholder')}
                            ref={addToRefs}
                            disabled={userSettings.UserType === 2 ? true : loading || saving}
                            formKey="SchoolName"
                        />
                        <LabeledInput
                            value={!!formState?.schoolInfo?.EmailAddress ? formState.schoolInfo.EmailAddress : ''}
                            labelText={t('SchoolInfo.SchoolEmailLabel')}
                            placeholder={t('SchoolInfo.SchoolEmailPlaceholder')}
                            ref={addToRefs}
                            formKey="EmailAddress"
                        />
                        <LabeledInput
                            value={!!formState?.schoolInfo?.WebsiteURL ? formState.schoolInfo.WebsiteURL : ''}
                            labelText={t('SchoolInfo.SchoolURLLabel')}
                            placeholder={t('SchoolInfo.SchoolURLPlaceholder')}
                            ref={addToRefs}
                            formKey="WebsiteURL"
                        />
                        <Phone
                            value={!!formState?.schoolInfo?.PhoneNumber ? formState.schoolInfo.PhoneNumber : ''}
                            labelText={t('SchoolInfo.SchoolPhoneLabel')}
                            placeholder={t('SchoolInfo.SchoolPhonePlaceholder')}
                            ref={addToRefs}
                            formKey="PhoneNumber"
                        />
                        <LabeledInput
                            value={!!formState?.schoolInfo?.Address1 ? formState.schoolInfo.Address1 : ''}
                            labelText={t('SchoolInfo.SchoolAddressLabel')}
                            placeholder={t('SchoolInfo.SchoolAddressPlaceholder')}
                            ref={addToRefs}
                            formKey="Address1"
                        />
                        <div className="row">
                            <LabeledInput
                                value={!!formState?.schoolInfo?.City ? formState.schoolInfo.City : ''}
                                labelText={t('SchoolInfo.SchoolCityLabel')}
                                placeholder={t('SchoolInfo.SchoolCityPlaceholder')}
                                ref={addToRefs}
                                formKey="City"
                            />
                            <LabeledSelect
                                value={!!formState?.schoolInfo?.StateAbbrev ? formState.schoolInfo.StateAbbrev : ''}
                                labelText={t('SchoolInfo.SchoolStateLabel')}
                                placeholder={t('SchoolInfo.SchoolStatePlaceholder')}
                                ref={addToRefs}
                                formKey="StateAbbrev"
                            >
                                {optionJSX}
                            </LabeledSelect>
                            <LabeledInput
                                value={!!formState?.schoolInfo?.ZipCode ? formState.schoolInfo.ZipCode : ''}
                                labelText={t('SchoolInfo.SchoolZipLabel')}
                                placeholder={t('SchoolInfo.SchoolZipPlaceholder')}
                                ref={addToRefs}
                                formKey="ZipCode"
                            />
                        </div>
                        <span>{t('SchoolInfo.AffidavitLabel')}</span>
                        <FileDrop
                            files={affidavit}
                            ref={addToRefs}
                            required={false}
                            formKey="Affidavit"
                            drop={handleDrop}
                            dragover={handleDrag}
                            remove={handleRemove}
                            disabled={loading || saving || fileSaving}
                        />
                    </PanelSection>
                    <PanelSection
                        classes={['contact-info']}
                        headerText={t('SchoolInfo.ContactInformationHeader')}
                    >
                        <Name
                            value={!!formState?.schoolInfo?.ContactName ? formState.schoolInfo.ContactName : ''}
                            labelText={t('SchoolInfo.ContactNameLabel')}
                            ref={addToRefs}
                            formKey="ContactName"
                        />
                        <Email
                            value={!!formState?.schoolInfo?.ContactEmail ? formState.schoolInfo.ContactEmail : ''}
                            labelText={t('SchoolInfo.ContactEmailLabel')}
                            ref={addToRefs}
                            formKey="ContactEmail"
                        />
                        <Phone
                            value={!!formState?.schoolInfo?.ContactPhoneNumber ? formState.schoolInfo.ContactPhoneNumber : ''}
                            labelText={t('SchoolInfo.ContactPhoneLabel')}
                            ref={addToRefs}
                            formKey="ContactPhoneNumber"
                        />
                    </PanelSection>
                    <PanelSection
                        classes={['quarter-info']}
                        headerText="Quarter Information"
                    >
                        <div className="grid-2">
                            {Array.from({ length: 4 }, (v, i) => i + 1).map((ent) => {
                                return (
                                    <div>
                                        Quarter {ent}
                                        <div className="row">
                                            <Box>
                                                <Typography sx={dateLabelSx}><RequiredMarker /> Start Date</Typography>
                                                <DatePicker
                                                    value={quarterDateState[`quarter${ent}StartUtc`]}
                                                    formKey={`Quarter${ent}StartUtc`}
                                                    disabled={saving || loading}
                                                    size="small"
                                                    textFieldSx={dateTextFieldSx}
                                                    clearable
                                                    onChange={(date) => handleDateChange(date, `quarter${ent}StartUtc`)}
                                                    onBlur={(event) => {
                                                        if (event?.type === 'blur') {
                                                            handleDateBlur(`quarter${ent}StartUtc`);
                                                        } else {
                                                            //Called when a date is selected from the calendar, only valid dates are enabled.
                                                            handleDateError(null, event, `quarter${ent}StartUtc`);
                                                        }
                                                    }}
                                                    onError={(err, date) => handleDateError(err, date, `quarter${ent}StartUtc`)}
                                                    error={
                                                        quarterDateState[`show_quarter${ent}StartUtcError`] &&
                                                        quarterDateState.errors?.find((err) => err?.key === `quarter${ent}StartUtc`)
                                                            ?.inError
                                                    }
                                                />
                                                <RenderIf
                                                    isTrue={
                                                        quarterDateState[`show_quarter${ent}StartUtcError`] &&
                                                        quarterDateState.errors?.find((err) => err?.key === `quarter${ent}StartUtc`)
                                                            ?.inError
                                                    }
                                                >
                                                    <Typography
                                                        sx={(theme) => {
                                                            return {
                                                                color: theme.palette.error.main,
                                                                fontSize: theme.spacing(1.35),
                                                            };
                                                        }}
                                                    >
                                                        {
                                                            quarterDateState.errors.find((err) => err?.key === `quarter${ent}StartUtc`)
                                                                ?.errorMessage
                                                        }
                                                    </Typography>
                                                </RenderIf>
                                            </Box>
                                            <div className="dash">&mdash;</div>

                                            <Box>
                                                <Typography sx={dateLabelSx}><RequiredMarker /> End Date</Typography>
                                                <DatePicker
                                                    value={quarterDateState[`quarter${ent}EndUtc`]}
                                                    formKey={`Quarter${ent}EndUTC`}
                                                    disabled={saving || loading}
                                                    size="small"
                                                    textFieldSx={dateTextFieldSx}
                                                    clearable
                                                    onChange={(date) => handleDateChange(date, `quarter${ent}EndUtc`)}
                                                    onBlur={(event) => {
                                                        if (event?.type === 'blur') {
                                                            handleDateBlur(`quarter${ent}EndUtc`);
                                                        } else {
                                                            //Called when a date is selected from the calendar, only valid dates are enabled.
                                                            handleDateError(null, event, `quarter${ent}EndUtc`);
                                                        }
                                                    }}
                                                    onError={(err, date) => handleDateError(err, date, `quarter${ent}EndUtc`)}
                                                    error={
                                                        quarterDateState[`show_quarter${ent}EndUtcError`] &&
                                                        quarterDateState.errors?.find((err) => err?.key === `quarter${ent}EndUtc`)?.inError
                                                    }
                                                />
                                                <RenderIf
                                                    isTrue={
                                                        quarterDateState[`show_quarter${ent}EndUtcError`] &&
                                                        quarterDateState.errors?.find((err) => err?.key === `quarter${ent}EndUtc`)?.inError
                                                    }
                                                >
                                                    <Typography
                                                        sx={(theme) => {
                                                            return {
                                                                color: theme.palette.error.main,
                                                                fontSize: theme.spacing(1.35),
                                                            };
                                                        }}
                                                    >
                                                        {
                                                            quarterDateState.errors.find((err) => err?.key === `quarter${ent}EndUtc`)
                                                                ?.errorMessage
                                                        }
                                                    </Typography>
                                                </RenderIf>
                                            </Box>

                                            {/* <LabeledInput
                                                value={
                                                    !!formState?.schoolInfo?.[`Quarter${ent}NumDays`]
                                                        ? formState.schoolInfo[`Quarter${ent}NumDays`]
                                                        : ''
                                                }
                                                labelText="School Days"
                                                validators={daysValidators}
                                                validated={true}
                                                ref={addToRefs}
                                                formKey={`Quarter${ent}NumDays`}
                                            /> */}
                                            <MuiStyledInput
                                                value={quarterDateState[`quarter${ent}NumDays`]}
                                                onChange={(event) => handleDayChange(event, `quarter${ent}NumDays`)}
                                                id={`school-days-${ent}`}
                                                label="School Days"
                                                size="small"
                                                typographySxObj={dateLabelSx}
                                                textFieldSxObj={theme => {
                                                    return {
                                                        ...dateTextFieldSx,
                                                        width: '5rem',
                                                    };
                                                }}
                                                inputProps={{
                                                    'data-form-key': `Quarter${ent}NumDays`,
                                                    ref: addToRefs,
                                                }}
                                                disabled={saving || loading}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </PanelSection>
                    <PanelSection
                        classes={['tuition-fees']}
                        headerText="Tuition and Fees"
                    >
                        <ReactMarkdown>{t('SchoolInfo.TuitionAndFeesInstructions1')}</ReactMarkdown>
                        <ReactMarkdown>{t('SchoolInfo.TuitionAndFeesInstructions2')}</ReactMarkdown>

                        <FeesTable
                            schoolId={userSettings.SchoolId}
                            saving={saving}
                        />
                    </PanelSection>
                </Panel>
            </Form>
        </View>
    );
}
